import React from 'react';
import { Users, User, Building, ShieldCheck, CheckCircle2, BookmarkCheck } from 'lucide-react';

interface UserRolesGuideSectionProps {
  user: any;
  onOpenRegister: () => void;
  onNavigateToDashboard: () => void;
  onExploreDirectory?: () => void;
}

export const UserRolesGuideSection: React.FC<UserRolesGuideSectionProps> = ({
  user,
  onOpenRegister,
  onNavigateToDashboard,
  onExploreDirectory,
}) => {
  return (
    <section className="w-full max-w-7xl 2xl:max-w-[1536px] 3xl:max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-8 2xl:px-10 space-y-10 2xl:space-y-12">
      <div className="text-center max-w-3xl 2xl:max-w-4xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs sm:text-sm 2xl:text-base font-semibold shadow-2xs">
          <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 2xl:w-5 2xl:h-5 text-indigo-600" />
          <span>Panduan Hak Akses & Mekanisme Pengajuan</span>
        </div>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl 2xl:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight">
          3 Tipe Pengguna & Alur Registrasi UIBN
        </h2>
        <p className="text-sm sm:text-base md:text-lg 2xl:text-xl text-slate-600 leading-relaxed">
          Pahami alur pendaftaran naskah berdasarkan hak akses: Penulis Mandiri, Lembaga Penerbit Resmi, serta Organisasi & Komunitas Literasi.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 2xl:gap-8">
        {/* For Authors (Penulis) */}
        <div className="bg-white p-6 sm:p-7 2xl:p-8 rounded-3xl border border-slate-200/90 shadow-2xs space-y-5 hover:border-indigo-300 transition flex flex-col justify-between">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 2xl:w-12 2xl:h-12 rounded-2xl bg-sky-50 text-sky-600 flex items-center justify-center">
                <User className="w-5 h-5 2xl:w-6 2xl:h-6" />
              </div>
              <span className="text-xs sm:text-sm 2xl:text-base font-bold px-3 py-1 rounded-full bg-sky-50 text-sky-700 border border-sky-200">
                1. Penulis Mandiri
              </span>
            </div>

            <div>
              <h3 className="text-base sm:text-lg lg:text-xl 2xl:text-2xl font-bold text-slate-900">Penulis Mandiri</h3>
              <p className="text-xs sm:text-sm lg:text-base 2xl:text-[17px] text-slate-600 mt-1 leading-relaxed">
                Penulis memiliki 2 pilihan fleksibel saat mendaftarkan naskah:
              </p>
            </div>

            {/* 2 Sub Paths */}
            <div className="space-y-2.5 pt-1">
              <div className="p-3 2xl:p-3.5 rounded-xl bg-sky-50/60 border border-sky-100 text-xs sm:text-sm text-slate-700">
                <div className="font-bold text-sky-900 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 2xl:w-4 2xl:h-4 text-sky-600 shrink-0" />
                  <span>A. Jalur Mandiri (Self-Publishing)</span>
                </div>
                <p className="text-[11px] sm:text-xs text-slate-600 mt-0.5 leading-relaxed">
                  Naskah langsung masuk ke Admin Pusat UIBN tanpa perantara penerbit.
                </p>
              </div>

              <div className="p-3 2xl:p-3.5 rounded-xl bg-purple-50/60 border border-purple-100 text-xs sm:text-sm text-slate-700">
                <div className="font-bold text-purple-900 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 2xl:w-4 2xl:h-4 text-purple-600 shrink-0" />
                  <span>B. Jalur Penerbit Terdaftar</span>
                </div>
                <p className="text-[11px] sm:text-xs text-slate-600 mt-0.5 leading-relaxed">
                  Pilih penerbit mitra untuk dikurasi lalu diteruskan ke Admin UIBN.
                </p>
              </div>
            </div>

            <p className="text-[11px] text-slate-500 italic bg-slate-50 p-2.5 rounded-xl border border-slate-200">
              *Penulis tidak berafiliasi lembaga/organisasi dan fokus pada hak cipta naskah pribadi.
            </p>
          </div>

          <div className="pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={() => {
                if (!user) {
                  onOpenRegister();
                } else {
                  onNavigateToDashboard();
                }
              }}
              className="w-full py-3 2xl:py-3.5 bg-sky-50 hover:bg-sky-100 text-sky-700 rounded-xl text-xs sm:text-sm 2xl:text-base font-semibold transition flex items-center justify-center gap-2 cursor-pointer"
            >
              <User className="w-4 h-4 2xl:w-5 2xl:h-5" />
              <span>{user ? 'Buka Dasbor Penulis' : 'Daftar Penulis Mandiri'}</span>
            </button>
          </div>
        </div>

        {/* For Publishers (Penerbit) */}
        <div className="bg-white p-6 sm:p-7 2xl:p-8 rounded-3xl border border-slate-200/90 shadow-2xs space-y-5 hover:border-indigo-300 transition flex flex-col justify-between">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 2xl:w-12 2xl:h-12 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center">
                <Building className="w-5 h-5 2xl:w-6 2xl:h-6" />
              </div>
              <span className="text-xs sm:text-sm 2xl:text-base font-bold px-3 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                2. Penerbit Resmi
              </span>
            </div>

            <div>
              <h3 className="text-base sm:text-lg lg:text-xl 2xl:text-2xl font-bold text-slate-900">Penerbit Resmi</h3>
              <p className="text-xs sm:text-sm lg:text-base 2xl:text-[17px] text-slate-600 mt-1 leading-relaxed">
                Penerbit berbadan hukum yang telah diverifikasi resmi oleh UIBN:
              </p>
            </div>

            <div className="p-3 2xl:p-3.5 rounded-xl bg-purple-50/70 border border-purple-100 text-xs sm:text-sm text-slate-700 space-y-1.5">
              <div className="font-bold text-purple-900 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 2xl:w-4 2xl:h-4 text-purple-600 shrink-0" />
                <span>Pendaftaran Langsung & Prefiks Unik</span>
              </div>
              <p className="text-[11px] sm:text-xs text-slate-600 leading-relaxed">
                Mendapatkan kode prefiks 4-digit resmi, hak menerbitkan buku langsung ke Admin UIBN, serta kurasi naskah penulis mitra.
              </p>
            </div>

            <p className="text-[11px] text-slate-500 italic bg-slate-50 p-2.5 rounded-xl border border-slate-200">
              *Nama penerbit dikunci setelah diverifikasi dan disetujui untuk menjaga keaslian data.
            </p>
          </div>

          <div className="pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={() => {
                if (!user) {
                  onOpenRegister();
                } else {
                  onNavigateToDashboard();
                }
              }}
              className="w-full py-3 2xl:py-3.5 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-xl text-xs sm:text-sm 2xl:text-base font-semibold transition flex items-center justify-center gap-2 cursor-pointer"
            >
              <Building className="w-4 h-4 2xl:w-5 2xl:h-5" />
              <span>{user ? 'Buka Dasbor Penerbit' : 'Daftar Akun Penerbit'}</span>
            </button>
          </div>
        </div>

        {/* For Organizations / Communities */}
        <div className="bg-white p-6 sm:p-7 2xl:p-8 rounded-3xl border border-slate-200/90 shadow-2xs space-y-5 hover:border-indigo-300 transition flex flex-col justify-between">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="w-10 h-10 2xl:w-12 2xl:h-12 rounded-2xl bg-amber-50 text-amber-700 flex items-center justify-center">
                <Users className="w-5 h-5 2xl:w-6 2xl:h-6" />
              </div>
              <span className="text-xs sm:text-sm 2xl:text-base font-bold px-3 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                3. Organisasi & Komunitas
              </span>
            </div>

            <div>
              <h3 className="text-base sm:text-lg lg:text-xl 2xl:text-2xl font-bold text-slate-900">Organisasi & Komunitas</h3>
              <p className="text-xs sm:text-sm lg:text-base 2xl:text-[17px] text-slate-600 mt-1 leading-relaxed">
                Yayasan, komunitas literasi, institusi pendidikan, dan lembaga riset:
              </p>
            </div>

            <div className="p-3 2xl:p-3.5 rounded-xl bg-amber-50/70 border border-amber-100 text-xs sm:text-sm text-slate-700 space-y-1.5">
              <div className="font-bold text-amber-900 flex items-center gap-1.5">
                <BookmarkCheck className="w-3.5 h-3.5 2xl:w-4 2xl:h-4 text-amber-600 shrink-0" />
                <span>Pendaftaran Langsung ke Admin UIBN</span>
              </div>
              <p className="text-[11px] sm:text-xs text-slate-600 leading-relaxed">
                Dapat langsung mendaftar UIBN ke admin seperti penerbit setelah verifikasi saat pertama kali mendaftar buku baru.
              </p>
            </div>

            <p className="text-[11px] text-slate-500 italic bg-slate-50 p-2.5 rounded-xl border border-slate-200">
              *Nama organisasi yang didaftarkan akan dikunci dan diverifikasi oleh admin UIBN.
            </p>
          </div>

          <div className="pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={() => {
                if (!user) {
                  onOpenRegister();
                } else {
                  onNavigateToDashboard();
                }
              }}
              className="w-full py-3 2xl:py-3.5 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded-xl text-xs sm:text-sm 2xl:text-base font-semibold transition flex items-center justify-center gap-2 cursor-pointer"
            >
              <Users className="w-4 h-4 2xl:w-5 2xl:h-5" />
              <span>{user ? 'Buka Dasbor Organisasi' : 'Daftar Akun Organisasi'}</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
